/**
 * 03 / PROCESS workflow + 04 / CRAFT proof points (plan/04 §03–04, plan/05 §4).
 * Every claim is grounded in the product docs (~/Downloads/ppfengine/docs).
 *
 * The Process section is a five-card strip made from ONE real Volvo FH Globe
 * window-adjacent panel: its STL scan and the matching PPF Engine SVG flatten/
 * export. This is the live-site content (deployed) — restored verbatim; the panel
 * renders (`workflow-*`, transparent) sit on a dark card the way they were shot.
 * Copy is installer-facing, no algorithm names, no em dashes.
 */

export interface WorkflowStage {
	index: string;
	title: string;
	body: string;
	/** The tiny mono readout in the card footer. */
	meta: string;
	/** Transparent panel render in `static/img/` — dark-card native. */
	asset: string;
	alt: string;
}

export const workflowStages: WorkflowStage[] = [
	{
		index: '01',
		title: 'Import scan',
		body: 'Bring the panel mesh in from your scanner and keep the real surface as the source.',
		meta: 'STL · 21,090 faces',
		asset: 'workflow-import',
		alt: 'Wireframe rendered from the real Volvo FH Globe window-adjacent panel STL scan.'
	},
	{
		index: '02',
		title: 'Clean mesh',
		body: 'Remove scan noise and prepare an even, workable surface before pattern work begins.',
		meta: 'Same Volvo panel',
		asset: 'workflow-clean',
		alt: 'Shaded surface rendered from the same real Volvo FH Globe STL scan.'
	},
	{
		index: '03',
		title: 'Flatten panel',
		body: 'Turn the cleaned scan into a workable two-dimensional mesh while preserving the panel shape.',
		meta: 'Actual flat mesh',
		asset: 'workflow-flatten',
		alt: 'The matching PPF Engine flattened mesh and its fitted panel boundary.'
	},
	{
		index: '04',
		title: 'Stretch & sag',
		body: 'Preview likely pull and compression zones from the panel geometry before cutting film.',
		meta: 'Geometry heat map',
		asset: 'workflow-regions',
		alt: 'Geometry-derived heat map of the real Volvo panel, from recessed sag areas to crowned high-pull areas.'
	},
	{
		index: '05',
		title: 'Fit & export',
		body: 'Fit a clean boundary, refine the anchors and send a full-scale file to the plotter.',
		meta: 'SVG · DXF · PLT',
		asset: 'workflow-export',
		alt: 'The actual fitted cut boundary from the matching PPF Engine SVG export, with editable anchors.'
	}
];

/** 03 / PROCESS section framing (live-site copy). */
export const process = {
	index: '04',
	label: 'Process',
	heading: 'Built for the real PPF workflow.',
	intro:
		'One real Volvo FH Globe panel, from the STL scan on your computer to its matching PPF Engine cut boundary.',
	/** Right-hand eyebrow + the source-trail line under the strip. */
	source: 'Real source · Volvo FH Globe panel',
	sourceTrail:
		'Globe window-adjacent panel · real STL scan · 21,090 faces · matching PPF Engine SVG export'
} as const;

/** 04 / CRAFT — why trust the geometry (plan/04 §04, plan/05 §4). */
export const craft = {
	index: '05',
	label: 'Craft',
	heading: 'A design tool, not a converter.',
	paragraphs: [
		'A converter would hand you the scan flattened, errors and all. PPF Engine shows every call it makes: where the seam runs, where the film stretches, where a relief cut saves the install. And it lets you overrule any of it.',
		'The output is not a tracing. It is a template. Below is the pre-flight for the same Volvo FH trim you just watched flatten: 23,849 scan faces in one pass, a clean cut path fitted with anchors you can grab, edges kept dimension-true, and the stretch graded before any film comes off the roll.'
	],
	/** Real numbers from the A-pillar-trim flatten shown above the readouts. */
	proof: [
		{ label: 'Faces flattened', value: '23,849 · one pass' },
		{ label: 'Within 8% stretch', value: '> 99.9% by area' },
		{ label: 'Fits the panel', value: 'measured, not traced' }
	]
} as const;

/**
 * 05 / CRAFT — the toolbox grid. The named tools an installer reaches for,
 * each in one sentence of shop language, with a small technical glyph and a
 * one-word readout so every tile carries the instrument voice.
 */
export interface CraftTool {
	/** Which inline glyph the tile draws (see Craft.svelte's glyph snippet). */
	glyph: 'wrap' | 'detect' | 'brush' | 'relief' | 'prestretch' | 'seam';
	title: string;
	line: string;
	/** Tiny mono readout in the tile footer: label + live-style value. */
	readout: { label: string; value: string };
}

export const craftToolsIntro =
	'The tools are the ones an installer actually reaches for, named the way you would say them across the shop:';

export const craftTools: CraftTool[] = [
	{
		glyph: 'wrap',
		title: 'Edge wrap',
		line: 'Type the wrap in millimetres: plus wraps past the edge, minus pulls the cut inboard, 0 cuts at the scan edge.',
		readout: { label: 'Edge return', value: 'mm, signed' }
	},
	{
		glyph: 'detect',
		title: 'Feature detection',
		line: 'Creases, character lines and openings are found in the scan and kept as guides, so seams and trim lines land on the panel, not near it.',
		readout: { label: 'Guides', value: 'from the scan' }
	},
	{
		glyph: 'brush',
		title: 'Stretch brush',
		line: 'Paint where you would pull the film; the template compensates before the plotter ever runs.',
		readout: { label: 'Compensation', value: 'painted on' }
	},
	{
		glyph: 'relief',
		title: 'Relief cuts',
		line: 'Drop a relief cut where the film fights back and watch the stress drain out of the stretch map.',
		readout: { label: 'Stress', value: 'shown live' }
	},
	{
		glyph: 'prestretch',
		title: 'Pre-stretch',
		line: 'Dial in pre-stretch per zone, the way you actually rack the film on the panel.',
		readout: { label: 'Zones', value: 'per panel' }
	},
	{
		glyph: 'seam',
		title: 'Seams on the surface',
		line: 'Three seam tools drawn straight on the 3D panel, exactly where the film should break.',
		readout: { label: 'Seam tools', value: '3 kinds' }
	}
];
